<template>
  <div class="car-details">
    <div class="car-details__content-wrap">
      <div class="car-details__content-inner">
        <MainCarDetails
          :is-mobile="isMobile"
          :car-details="carDetails"
          :current-order-step="currentOrderStep"
          :car="car"
          :order="order"
          @openModal="openModal"
          @closeModal="closeModal"
        />
        <SidebarSummary
          v-if="!editMode && !isLastTwoSteps"
          :order="order"
          :car="car"
          :current-order-step="currentOrderStep"
          title="Zusammenfassung"
          :hide-details="true"
          :financing-edited="financingEdited"
          @editData="$emit('editData')"
          @editApproval="$emit('editApproval')"
          @editDelivery="$emit('editDelivery')"
          @editFinancing="$emit('editFinancing')"
          @editWarranty="$emit('editWarranty')"
          @editFridayWarranty="$emit('editFridayWarranty')"
        />
        <div v-if="isFinancingPaymentMethod && isTooltipOpen && isLastTwoSteps" class="financing-payment_hint">
          <p class="tipp-text">
            <b><i class="fa fa-bell"></i> Wichtig </b><br />
            <p>Nach Klick auf “Bestellung Abschließen” werden Sie automatisch auf die 
              Online-Antragsstrecke unserer Partnerbank weitergeleitet. Den 
              Kaufvertrag erhalten Sie nach genehmigter Finanzierung von uns per Mail.</p>
          </p>
        </div>
        <div v-if="!isFinancingPaymentMethod && isTooltipOpen && isLastTwoSteps" class="car-details__notes">
          <p class="tipp">
            <b>Übrigens!</b><br />
            {{ hintText.text }}
          </p>
          <p class="tipp-text">
            <b><i class="fa fa-bell"></i> Unser Tipp</b><br />
            {{ hintText.hint }}
          </p>
        </div>
        <div v-if="showFinishBtn" class="car-details__create-finish">
          <button @click="finalizeOrder" type="button">
            {{ submitBtnText }}
          </button>
        </div>
      </div>
      <car-details-modal
        :car="car"
        :car-details="carDetails"
        :is-open="showModal"
        @close="closeModal"
      />
      <CreditCheckModal :is-open="showCreditModal" @close="closeCreditModal" />
    </div>
  </div>
</template>

<script>
import moment from "moment";
import CreditCheckModal from "@/components/modals/CreditCheckModal";
import CarDetailsModal from "./modals/CarDetailsModal";
import MainCarDetails from "./MainCarDetails";
import SidebarSummary from "./SidebarSummary";

const financingText = {
  text:
    "Sie erhalten nach Abschluss der Bestellung einen Link zum Finanzierungsantrag von unserer Partner-Bank.",
  hint:
    "Damit wir Ihr Wunschfahrzeug für Sie reservieren können, überprüfen Sie nach der Bestellung Ihr Postfach und füllen Sie den Finanzierungsantrag direkt aus.",
};

const selfPayText = {
  text:
    "Sie haben sich ein Fahrzeug mit einem der besten Preise in Deutschland ausgesucht.",
  hint:
    "Um sicherzustellen, dass Ihnen kein/e andere/r Käufer*in zuvorkommt, überweisen Sie direkt nach Rechnungsstellung via Online-Direktzahlung (Direct Payment).",
};

const selfPayFinishText =
  'Je nach der von Ihnen angegebenen Kontaktpräferenz schicken wir Ihnen nach Klick auf "Jetzt verbindlich bestellen" innerhalb von einem Werktag den Kaufvertrag per Mail. Wenn Sie ein Beratungstelefonat wünschen, erhalten Sie den Kaufvertrag nach Abschluss des Telefonats ebenfalls per Mail.';
const financingFinishText =
  'Schließen Sie die Finanzierung ganz einfach und bequem online ab. Nach Klick auf "Zum Finanzierungsantrag" werden Sie hierzu auf die Online-Antragsstrecke unserer Partnerbank weitergeleitet. Den Kaufvertrag erhalten Sie nach genehmigter Finanzierung von uns per Mail.';

export default {
  components: {
    CarDetailsModal,
    MainCarDetails,
    SidebarSummary,
    CreditCheckModal,
  },
  props: [
    "car",
    "order",
    "showFinishBtn",
    "currentOrderStep",
    "editMode",
    "financingEdited",
  ],
  data() {
    return {
      showModal: false,
      showCreditModal: false,
      agreements: false,
      agreementsError: "",
      isTooltipOpen: false,
    };
  },
  computed: {
    isFinancingPaymentMethod() {
      return this.order && this.order.payment_method !== "self_pay";
    },
    isMobile() {
      return window.innerWidth <= 520;
    },
    isLastTwoSteps() {
      return this.currentOrderStep === "6" || this.currentOrderStep === "7";
    },
    carDetails() {
      if (this.order) {
        return {
          ...this.order,
          main_image_url: this.car.main_image.full,
          consumption: this.order.car_info.emission.consumption.average
            ? `${this.order.car_info.emission.consumption.average} l / 100 km`
            : null,
          emission: this.order.car_info.emission.co2_emission
            ? `${this.order.car_info.emission.co2_emission}g CO₂ / km`
            : null,
          electro: this.car.emission.consumption.electro
            ? `${this.car.emission.consumption.electro}  kWh / 100 km`
            : null,
          consumption_co2_emission_data: this.car.emission.consumption_co2_emission_data,
          wltp_electric_motor_range: this.car.emission.wltp.wltp_electric_motor_range,
        };
      }
      const registration = moment(
        this.car.initial_registration,
        "YYYY-MM-DD"
      ).format("MM/YYYY");
      return {
        main_image_url: this.car.main_image.full,
        headline: this.car.name,
        mileage_km: `${this.car.mileage} km`,
        registration_date: registration,
        power: `${this.car.power.kw} kW (${this.car.power.hp} PS)`,
        fueltype_name: this.car.fueltype.name,
        geartype_name: this.car.geartype.name,
        consumption: this.car.emission.consumption.average
          ? `${this.car.emission.consumption.average} l / 100 km`
          : null,
        emission: this.car.emission.co2_emission
          ? `${this.car.emission.co2_emission}g CO₂ / km`
          : null,
        electro: this.car.emission.consumption.electro
          ? `${this.car.emission.consumption.electro}  kWh / 100 km`
          : null,
          consumption_co2_emission_data: this.car.emission.consumption_co2_emission_data,
          wltp_electric_motor_range: this.car.emission.wltp.wltp_electric_motor_range,
      };
    },
    hintText() {
      return this.order.payment_method === "self_pay"
        ? selfPayText
        : financingText;
    },
    finishText() {
      return this.order.payment_method === "self_pay"
        ? selfPayFinishText
        : financingFinishText;
    },
    submitBtnText() {
      return this.order.payment_method === "self_pay"
        ? "Jetzt verbindlich bestellen"
        : "Bestellung abschließen";
    },
  },
  watch: {
    agreements(val) {
      if (val && this.agreementsError) {
        this.agreementsError = "";
      }
    },
    currentOrderStep(val) {
      if (this.isLastTwoSteps) {
        this.openTooltip();
      }
    },
  },
  mounted() {
    if (this.isLastTwoSteps) {
      this.openTooltip();
    }
  },
  methods: {
    openModal() {
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    openCreditModal() {
      this.showCreditModal = true;
    },
    closeCreditModal() {
      this.showCreditModal = false;
    },
    finalizeOrder() {
      this.$emit("finalizeOrderCreate");
    },
    closeTooltip() {
      this.isTooltipOpen = false;
    },
    openTooltip() {
      this.isTooltipOpen = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.hint {
  margin-top: 20px;
  margin-bottom: 20px;
  line-height: 24px;
  color: #000;
  background: rgba($red, 0.1);
  padding: 25px 20px;
  font-size: 14px;
  text-align: center;
}

.car-details {
  max-width: 450px;
  margin: 2rem auto;

  &__notes {
    position: relative;
    line-height: 28px;
    color: $blue;

    .benefits-link {
      display: block;
      margin-top: 20px;
      padding: 10px 5px;
      font-size: 16px;
      font-weight: 600;
      text-align: center;
      background-color: $white;
      color: $darkgray;
      border-radius: 50px;
    }

    button {
      position: absolute;
      right: 10px;
      top: 10px;
      display: none;
      color: $white;
    }

    p {
      &:last-child {
        margin-top: 20px;
      }
    }

    @include breakpoint(medium) {
      margin: 1rem 0 0 0;
    }

    @include breakpoint(desktop) {
      button {
        display: block;
      }

      p {
        margin-bottom: 20px;
        font-size: 16px;
        line-height: 24px;
      }
    }
  }
  &__checkbox {
    margin: 20px 10px;
    display: block;
    > div {
      display: flex;
      align-items: center;
      font-size: 14px;
      line-height: 20px;
    }
    input[type="checkbox"] {
      margin-right: 10px;
    }
    a,
    button {
      color: $red;
    }
    p {
      margin-left: 20px;
    }
    &.with-error {
      .error {
        margin-left: 0;
        padding: 5px 0;
        font-size: 12px;
        line-height: 12px;
        color: $red;
      }
      input[type="checkbox"] + label:before {
        border-color: $red;
      }
    }
    @include breakpoint(medium) {
      margin: 20px 0;
    }
  }
  h3 {
    padding: 0.9375rem 0.625rem 0.3125rem 0.625rem;
    font-size: 0.75rem;
  }
  &__create-finish {
    position: sticky;
    bottom: 0;
    left: 0;
    background: white;
    margin: 1rem 0;
    z-index:3;

    button {
      position: relative;
      display: block;
      width: 100%;
      padding: 15px 10px;
      margin-bottom: 15px;
      font-size: 16px;
      font-weight: 600;
      text-align: center;
      background-color: $red;
      color: $white;
      transition: all 0.5s;
      border-radius: 50px;
      &:after {
        @include icon("\f105", $white, 18px);
      }
      &:hover,
      &:focus,
      &:active {
        background: $darkgray;
      }
    }
    p {
      font-size: 13px;
      line-height: 18px;
    }
  }
  &__content-wrap {
    padding: 0 0.625rem;
    margin-bottom: 0.625rem;
  }
  &__price {
    text-align: right;
    margin-bottom: 1.25rem;
  }
  &__base-price {
    font-size: 1.75rem;
    font-weight: 600;
  }
  &__initial-price {
    font-size: 1.125rem;
    font-weight: 600;
    text-decoration: line-through;
    color: $red;
  }
  &__note {
    font-size: 0.75rem;
    line-height: 1rem;
  }
  @include breakpoint(tablet) {
    border-top: none;
    background-color: $white;
    h3 {
      padding: 0.9375rem 0 0.3125rem 0;
    }
    &__note {
      margin-top: 0.9375rem;
      text-align: center;
    }
  }
  @include breakpoint(medium) {
    max-width: 300px;
    width: 100%;
    margin: 0;
  }
  @include breakpoint(desktop) {
    padding: 0;
    max-width: 330px;
    &__content-wrap {
      padding: 0;
    }
    h3 {
      font-size: 0.875rem;
    }
  }

  .financing-payment_hint {
    padding: 18px;
    background: #F4F4F4;
  }
}

</style>
